import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Wallet, Wifi, WifiOff, Activity, DollarSign, TrendingUp, TrendingDown, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const fmtMoney = (v) => v != null ? `$${v.toLocaleString(undefined, {minimumFractionDigits:2,maximumFractionDigits:2})}` : '$0.00';
const fmtPnl = (v) => {
  if (v == null) return '$0.00';
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, {minimumFractionDigits:2,maximumFractionDigits:2})}`;
};
const pnlCls = (v) => v > 0 ? 'ops-long' : v < 0 ? 'ops-short' : 'text-[hsl(var(--ops-text))]';

export default function Accounts() {
  const { autoRefresh } = useOutletContext();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/ops/accounts");
        setAccounts(data.accounts || []);
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
    if (!autoRefresh) return;
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [autoRefresh]);

  if (loading) return (
    <div className="space-y-4"><Skeleton className="h-8 w-48 bg-[hsl(var(--ops-surface))]" /><div className="grid gap-4 lg:grid-cols-2">{[1,2].map(i=><Skeleton key={i} className="h-64 bg-[hsl(var(--ops-surface))]" />)}</div></div>
  );

  return (
    <div data-testid="ops-accounts-page" className="space-y-5">
      <div>
        <h1 className="text-lg font-display font-semibold text-[hsl(var(--ops-text))]">Accounts</h1>
        <p className="text-xs text-[hsl(var(--ops-text-muted))]">SIM and LIVE account management</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {accounts.map((acc, i) => (
          <Card key={i} data-testid={`ops-account-${acc.type?.toLowerCase()}`} className={`bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden ${acc.type === 'LIVE' ? 'opacity-60' : ''}`}>
            <div className="px-4 py-3 border-b border-[hsl(var(--ops-border))] flex items-center justify-between max-sm:flex-wrap max-sm:gap-2">
              <div className="flex items-center gap-2">
                <Wallet className="size-4 text-[hsl(var(--ops-brand))]" />
                <span className="text-sm font-semibold text-[hsl(var(--ops-text))] max-sm:break-words max-sm:min-w-0">{acc.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`rounded text-[10px] border-0 px-1.5 ${acc.type === 'LIVE' ? 'bg-[hsl(var(--ops-critical))]/15 text-[hsl(var(--ops-critical))]' : 'bg-[hsl(var(--ops-brand))]/15 text-[hsl(var(--ops-brand))]'}`}>
                  {acc.type}
                </Badge>
                <Badge className={`rounded text-[10px] border-0 px-1.5 ${acc.status === 'active' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-text-muted))]/15 text-[hsl(var(--ops-text-muted))]'}`}>
                  {acc.status?.toUpperCase()}
                </Badge>
                {acc.connected ? <Wifi className="size-3 text-[hsl(var(--ops-long))]" /> : <WifiOff className="size-3 text-[hsl(var(--ops-text-muted))]" />}
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-0.5">Balance</div>
                  <div className="text-base font-mono-num font-semibold text-[hsl(var(--ops-text))]">{fmtMoney(acc.balance)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-0.5">Net Liquidation</div>
                  <div className="text-base font-mono-num font-semibold text-[hsl(var(--ops-text))]">{fmtMoney(acc.net_liquidation)}</div>
                </div>
              </div>
              <Separator className="bg-[hsl(var(--ops-border))]" />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-0.5">Today P&L</div>
                  <div className={`text-sm font-mono-num font-semibold ${pnlCls(acc.pnl_today)}`}>{fmtPnl(acc.pnl_today)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-0.5">Unrealized</div>
                  <div className={`text-sm font-mono-num ${pnlCls(acc.pnl_unrealized)}`}>{fmtPnl(acc.pnl_unrealized)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-0.5">Realized</div>
                  <div className={`text-sm font-mono-num ${pnlCls(acc.pnl_realized)}`}>{fmtPnl(acc.pnl_realized)}</div>
                </div>
              </div>
              <Separator className="bg-[hsl(var(--ops-border))]" />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-0.5">Margin</div>
                  <div className="text-xs font-mono-num text-[hsl(var(--ops-text))]">{fmtMoney(acc.margin_used)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-0.5">Buying Power</div>
                  <div className="text-xs font-mono-num text-[hsl(var(--ops-text))]">{fmtMoney(acc.buying_power)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-0.5">Positions</div>
                  <div className="text-xs font-mono-num text-[hsl(var(--ops-text))]">{acc.open_positions}</div>
                </div>
              </div>
              {acc.type === 'LIVE' && acc.status === 'inactive' && (
                <div className="mt-2 p-2 rounded-lg bg-[hsl(var(--ops-surface-2))] text-center">
                  <p className="text-xs text-[hsl(var(--ops-text-muted))]"><ShieldCheck className="size-3 inline mr-1" />Live trading will be enabled once the SIM account proves consistent profitability.</p>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
